import { assertStaffCapability, assertStaffRelationship, authorizeStaff, isSupervisor, transitionActorId, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { assertMinimumIntent, validateRequestRequirements, } from "./concierge-request-requirements.js";
import { AuthorizationDeniedError, DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
import { conciergeRequestStates, conciergeRequestTerminalStates, conciergeRequestTransitions, isAllowedRequestTransition, isTerminalRequestState, requestInStateRefusal, } from "./request-lifecycle.js";
import { requestCustomerStatusText } from "./request-status-text.js";
// The states, the 26 moves and the in-state commands are data in `request-lifecycle.ts` (CHT-2002). They are re-exported here under the names
// that the server code already uses.
export { conciergeRequestStates, conciergeRequestTerminalStates, conciergeRequestTransitions, isAllowedRequestTransition, };
export const requestCreators = Object.freeze(["CUSTOMER", "STAFF"]);
/** The agent acting for a customer is `CUSTOMER` through the `CONCIERGE_AGENT` channel. */
export const requestChannels = Object.freeze([
    "CUSTOMER_APP",
    "CONCIERGE_AGENT",
    "STAFF_CONSOLE",
]);
export const requestTransitionChannels = Object.freeze([...requestChannels, "SYSTEM"]);
export const requestPriorities = Object.freeze(["STANDARD", "HIGH", "URGENT"]);
export const requestAssignmentSources = Object.freeze([
    "TEMPORARY",
    "PRIMARY",
    "BACKUP",
    "SUPERVISOR_QUEUE",
    "DIRECT",
]);
export const confirmationEvidenceKinds = Object.freeze([
    "SUPPLIER_WRITTEN_CONFIRMATION",
    "SUPPLIER_VERBAL_CONFIRMATION",
    "LAFAYETTE_DIRECT_FULFILMENT",
]);
export const completionEvidenceKinds = Object.freeze([
    "SERVICE_DELIVERED",
    "CUSTOMER_ACKNOWLEDGED",
    "SUPPLIER_REPORTED_FULFILMENT",
]);
export const requestClosureActorKinds = Object.freeze(["CUSTOMER", "STAFF", "SYSTEM"]);
export const requestEvents = Object.freeze([
    "CREATED",
    "DRAFT_UPDATED",
    "SUBMITTED",
    "ASSIGNED",
    "CLAIMED",
    "AWAITING_CUSTOMER",
    "CUSTOMER_RESPONDED",
    "RESPONSE_RECORDED",
    "PAYMENT_REQUIRED",
    "PAYMENT_RESOLVED",
    "PAYMENT_REPLANNED",
    "CONFIRMED",
    "COMPLETED",
    "CANCELLED",
    "UNAVAILABLE",
    "DECLINED",
    "EXPIRED",
    "OWNER_REASSIGNED",
    "PRIORITY_CHANGED",
    "STATUS_SUMMARY_UPDATED",
    "WORK_STATUS_CHANGED",
]);
/**
 * The staff-only progress marker of an IN_PROGRESS request (CHT-750): where the work waits, with no change of
 * the state. It is never shown to a customer. `NONE` outside IN_PROGRESS: the guard LF055 refuses anything else,
 * and every transition that leaves IN_PROGRESS resets it.
 */
export const requestWorkStatuses = Object.freeze([
    "NONE",
    "WITH_SUPPLIER",
    "INTERNAL_REVIEW",
]);
export const requestEvidenceKinds = Object.freeze([
    "ASSIGNMENT_DECISION",
    "CUSTOMER_RESPONSE",
    "STAFF_RECORDED_RESPONSE",
    "PAYMENT_REQUEST",
    "PAYMENT_OUTCOME",
    "PAYMENT_REPLAN",
    "SUPPLIER_CONFIRMATION",
    "OPERATIONAL_CLOSURE",
    "EXPIRY_POLICY",
]);
export const internalNoteKinds = Object.freeze([
    "SUPPLIER_CONTACT",
    "SUPPLIER_QUOTE_CONTEXT",
    "STAFF_NOTE",
    "HANDOVER",
]);
// --- Shared checks -------------------------------------------------------------------------------
const denied = (code) => new AuthorizationDeniedError(code);
const violation = (code) => new DomainRuleViolationError(code);
function requireReason(reason) {
    if (reason === null || reason === undefined)
        throw new DomainValidationError("reason", "is required");
    return reason;
}
function checkVersion(actor, expectedVersion, request) {
    if (expectedVersion === null) {
        if (actor.kind === "staff")
            throw new DomainValidationError("expectedVersion", "is required");
        return;
    }
    if (expectedVersion !== request.version)
        throw violation("request.version_conflict");
}
const STAFF_TOKEN_PRECEDENCE = ["O", "D", "F", "S"];
const BASIS = {
    O: "OWNER",
    D: "TARGET",
    F: "FALLBACK_CONCIERGE",
    S: "SUPERVISOR",
};
/** The staff token that lets the actor drive the rule on this request, or `undefined`. */
function staffTokenFor(actorRule, request, actor) {
    if (actorRule.staffCreatedOnly === true && request.createdBy !== "STAFF")
        return undefined;
    const held = (token) => token === "O"
        ? actor.relationship.isOwner
        : token === "D"
            ? actor.relationship.isTarget
            : token === "F"
                ? actor.relationship.hasEffectiveAssignment
                : isSupervisor(actor.authority);
    return STAFF_TOKEN_PRECEDENCE.find((candidate) => actorRule.staff.includes(candidate) && held(candidate));
}
/**
 * Step 1 for a staff actor, before any check of the request's state (R24). A staff user who holds
 * the role but has no relationship to the request must not learn its state class from the error,
 * so the refusal cannot depend on the state. The actor passes when at least one rule of the command
 * grants the role and the actor holds ANY staff relationship to the request (owner, target,
 * effective assignee or supervisor), whether or not that rule lists it. A related actor keeps the
 * state errors of the pair check. Otherwise this throws `relationship_missing`, or, when no rule
 * grants the role, the first role refusal (`staff_inactive`, `mfa_required`, `role_missing`). Every
 * rule of a command is read, whatever the request's state. A customer and a system actor are not
 * checked here.
 */
function assertStaffReachesRequest(rules, actor, environment) {
    if (actor.kind !== "staff")
        return;
    let roleRefusal = null;
    let roleGranted = false;
    for (const candidate of rules) {
        const decision = authorizeStaff(actor.authority, candidate.capability, environment);
        if (decision.allowed)
            roleGranted = true;
        else
            roleRefusal ??= denied(decision.code);
    }
    // `rules` is never empty for a real command; the `null` case only keeps the types total.
    if (!roleGranted && roleRefusal !== null)
        throw roleRefusal;
    assertStaffRelationship(actor.authority, actor.relationship, "RESOURCE");
}
/**
 * The actor check of the matched rule, after the state pair. A customer is checked for ownership
 * first (`not_owner` leaks nothing), a system actor against the rule's registry entries, and staff
 * by role (`authorizeStaff`) and then by relationship (`relationship_missing`). A staff actor has
 * already passed `assertStaffReachesRequest`, but a rule can allow fewer tokens than the actor holds.
 */
function authorizeActor(actorRule, request, actor, environment) {
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== request.customerId)
                throw denied("not_owner");
            if (!actorRule.customer)
                throw denied("role_missing");
            return { kind: "customer", actor };
        case "system":
            if (!actorRule.system.includes(actor.actorId))
                throw denied("role_missing");
            return { kind: "system", actor };
        case "staff": {
            assertStaffCapability(actor, actorRule.capability, environment);
            const token = staffTokenFor(actorRule, request, actor);
            if (token === undefined)
                throw denied("relationship_missing");
            return { kind: "staff", actor, basis: BASIS[token] };
        }
    }
}
const channelOf = (who) => who.kind === "customer" ? who.actor.channel : who.kind === "staff" ? "STAFF_CONSOLE" : "SYSTEM";
function history(who, request, to, event, rest = {}) {
    return Object.freeze({
        event,
        fromState: request.state,
        toState: to,
        actorKind: who.kind,
        actorId: transitionActorId(who.actor),
        channel: channelOf(who),
        evidenceKind: rest.evidenceKind ?? null,
        evidenceReference: rest.evidenceReference ?? null,
        evidencePaymentRequestId: rest.evidencePaymentRequestId ?? null,
        customerVisibleMessage: rest.customerVisibleMessage ?? null,
        internalReason: rest.internalReason ?? null,
    });
}
const staffUserIdOf = (who) => who.actor.authority.staffUserId;
/**
 * Plans one of the 26 allowed transitions, or refuses it. Checks run in this order, so the error
 * is deterministic: (0) for a staff actor only, the role and the relationship against every rule of
 * the command (`AuthorizationDeniedError`; see `assertStaffReachesRequest`), so a staff user with no
 * relationship to the request gets the same refusal in every state (R24), (1) the pair is allowed
 * (`request.terminal` from a terminal state, else `request.transition_invalid`; a command that does
 * not belong to the pair is also invalid), (2) the actor against the rule of the pair
 * (`AuthorizationDeniedError`), (3) `expectedVersion`, (4) the reason, then the evidence or minimum
 * intent. Nothing is written here: the caller applies the patch as a compare-and-set and appends
 * the history row in the same transaction.
 */
export function planRequestTransition(input) {
    const { request, to, command, actor, evidence, now } = input;
    assertStaffReachesRequest(conciergeRequestTransitions.filter((row) => row.command === command), actor, input.environment);
    if (isTerminalRequestState(request.state))
        throw violation("request.terminal");
    const matched = conciergeRequestTransitions.find((row) => row.from === request.state && row.to === to);
    if (matched === undefined || matched.command !== command)
        throw violation("request.transition_invalid");
    const who = authorizeActor(matched, request, actor, input.environment);
    checkVersion(actor, input.expectedVersion, request);
    const staffReason = who.kind === "staff" ? requireReason(evidence.staffReason) : null;
    const effect = buildEffect(matched, request, who, staffReason, evidence, now);
    const closure = effect.patch.kind === "close" ? effect.patch.closureActorKind : null;
    return Object.freeze({
        ruleId: matched.id,
        command,
        from: request.state,
        to,
        patch: effect.patch,
        history: effect.history,
        auditAction: effect.auditAction,
        basis: effect.basis ?? null,
        closureActorKind: closure,
    });
}
function buildEffect(matched, request, who, staffReason, evidence, now) {
    const to = matched.to;
    switch (matched.command) {
        case "submit": {
            assertMinimumIntent(request.requirements.requestType, request.requirements);
            return {
                patch: {
                    kind: "submit",
                    submittedAt: "now",
                    customerStatusSummary: requestCustomerStatusText("SUBMITTED"),
                },
                history: history(who, request, to, "SUBMITTED", { internalReason: staffReason }),
                auditAction: who.kind === "staff"
                    ? "concierge_request.submitted_by_staff"
                    : "concierge_request.submitted",
            };
        }
        case "assign": {
            if (who.kind === "system") {
                const decision = evidence.routing;
                if (decision === null || decision === undefined) {
                    throw new DomainValidationError("routing", "is required");
                }
                if (decision.kind === "UNASSIGNED")
                    throw violation("request.no_eligible_assignee");
                const named = "staffUserId" in decision;
                return {
                    patch: {
                        kind: "assign",
                        assignmentSource: decision.kind,
                        assignedStaffUserId: named ? decision.staffUserId : null,
                        routeAssignmentId: named ? decision.assignmentId : null,
                        assignedAt: "now",
                    },
                    history: history(who, request, to, "ASSIGNED", {
                        evidenceKind: "ASSIGNMENT_DECISION",
                        // The destination of the assignment is part of the immutable row: `ROUTE:<routing kind>:<staff id, or QUEUE>`.
                        evidenceReference: `ROUTE:${decision.kind}:${named ? decision.staffUserId : "QUEUE"}`,
                        internalReason: `assignment:${decision.kind.toLowerCase()}`,
                    }),
                    auditAction: "concierge_request.assigned",
                };
            }
            const direct = evidence.directAssignee;
            if (direct === null || direct === undefined) {
                throw new DomainValidationError("directAssignee", "is required");
            }
            if (!direct.eligible)
                throw violation("request.assignee_ineligible");
            return {
                patch: {
                    kind: "assign",
                    assignmentSource: "DIRECT",
                    assignedStaffUserId: direct.staffUserId,
                    routeAssignmentId: null,
                    assignedAt: "now",
                },
                history: history(who, request, to, "ASSIGNED", {
                    evidenceKind: "ASSIGNMENT_DECISION",
                    evidenceReference: `DIRECT:${direct.staffUserId}`,
                    internalReason: staffReason,
                }),
                auditAction: "concierge_request.assigned_by_supervisor",
            };
        }
        case "claim": {
            if (who.kind !== "staff")
                throw denied("role_missing");
            return {
                patch: { kind: "claim", ownerStaffUserId: staffUserIdOf(who), claimedAt: "now" },
                history: history(who, request, to, "CLAIMED", { internalReason: staffReason }),
                auditAction: "concierge_request.claimed",
                basis: who.basis,
            };
        }
        case "await_customer": {
            const question = boundedText("question", evidence.question, 1000, { multiline: true });
            return {
                patch: {
                    kind: "await_customer",
                    pendingCustomerQuestion: question,
                    awaitingCustomerSince: "now",
                    workStatus: "NONE",
                },
                history: history(who, request, to, "AWAITING_CUSTOMER", {
                    customerVisibleMessage: question,
                    internalReason: staffReason,
                }),
                auditAction: "concierge_request.awaiting_customer",
            };
        }
        case "respond": {
            const patch = {
                kind: "respond",
                pendingCustomerQuestion: null,
                awaitingCustomerSince: null,
            };
            if (who.kind === "customer") {
                const response = boundedText("response", evidence.response, 2000, { multiline: true });
                return {
                    patch,
                    history: history(who, request, to, "CUSTOMER_RESPONDED", {
                        evidenceKind: "CUSTOMER_RESPONSE",
                        customerVisibleMessage: response,
                    }),
                    auditAction: "concierge_request.customer_responded",
                };
            }
            const reference = boundedText("recordedResponseReference", evidence.recordedResponseReference, 200);
            return {
                patch,
                history: history(who, request, to, "RESPONSE_RECORDED", {
                    evidenceKind: "STAFF_RECORDED_RESPONSE",
                    evidenceReference: reference,
                    internalReason: staffReason,
                }),
                auditAction: "concierge_request.response_recorded_by_staff",
            };
        }
        case "require_payment": {
            const paymentRequestId = evidence.paymentRequestId;
            if (paymentRequestId === null || paymentRequestId === undefined) {
                throw new DomainValidationError("paymentRequestId", "is required");
            }
            if (evidence.paymentEligibility?.status !== "eligible") {
                throw violation("request.payment_request_ineligible");
            }
            return {
                patch: { kind: "require_payment", paymentRequestId, workStatus: "NONE" },
                history: history(who, request, to, "PAYMENT_REQUIRED", {
                    evidenceKind: "PAYMENT_REQUEST",
                    evidencePaymentRequestId: paymentRequestId,
                    internalReason: staffReason ?? "payment:required",
                }),
                auditAction: who.kind === "staff"
                    ? "concierge_request.payment_required"
                    : "concierge_request.payment_required_by_system",
            };
        }
        case "resolve_payment": {
            if (evidence.paymentRequestId === null ||
                evidence.paymentRequestId === undefined ||
                evidence.paymentRequestId !== request.paymentRequestId) {
                throw violation("request.payment_request_ineligible");
            }
            const outcome = requireOutcome(evidence.paymentOutcome);
            if (who.kind === "system") {
                if (outcome.status === "pending" || outcome.status === "not_found") {
                    throw violation("request.payment_outcome_pending");
                }
                return {
                    patch: { kind: "resume_payment" },
                    history: history(who, request, to, "PAYMENT_RESOLVED", {
                        evidenceKind: "PAYMENT_OUTCOME",
                        evidenceReference: outcome.reference,
                        evidencePaymentRequestId: evidence.paymentRequestId,
                        internalReason: `payment:${outcome.status}`,
                    }),
                    auditAction: "concierge_request.payment_resolved",
                };
            }
            // A replan waits for the payment to end badly: settled is paid, pending is in flight, and
            // not_found is nothing to go on. Only failed, expired or cancelled hand the work back.
            if (outcome.status === "settled")
                throw violation("request.payment_already_settled");
            if (outcome.status === "pending" || outcome.status === "not_found") {
                throw violation("request.payment_outcome_pending");
            }
            return {
                patch: { kind: "resume_payment" },
                history: history(who, request, to, "PAYMENT_REPLANNED", {
                    evidenceKind: "PAYMENT_REPLAN",
                    evidencePaymentRequestId: evidence.paymentRequestId,
                    internalReason: staffReason,
                }),
                auditAction: "concierge_request.payment_replanned",
            };
        }
        case "confirm": {
            if (who.kind !== "staff")
                throw denied("role_missing");
            const confirmation = evidence.confirmation;
            if (confirmation === null ||
                confirmation === undefined ||
                typeof confirmation.evidenceId !== "string" ||
                !confirmationEvidenceKinds.includes(confirmation.kind)) {
                throw new DomainValidationError("confirmation", "needs an evidence row and a known kind");
            }
            const reference = boundedText("confirmationReference", confirmation.reference, 200);
            return {
                patch: {
                    kind: "confirm",
                    workStatus: "NONE",
                    confirmationEvidenceId: confirmation.evidenceId,
                    confirmationEvidenceKind: confirmation.kind,
                    confirmationEvidenceReference: reference,
                    confirmedByStaffUserId: staffUserIdOf(who),
                    confirmedAt: "now",
                },
                history: history(who, request, to, "CONFIRMED", {
                    evidenceKind: "SUPPLIER_CONFIRMATION",
                    evidenceReference: reference,
                    internalReason: staffReason,
                }),
                auditAction: "concierge_request.confirmed",
            };
        }
        case "complete": {
            if (who.kind !== "staff")
                throw denied("role_missing");
            const completion = evidence.completion;
            if (completion === null ||
                completion === undefined ||
                !completionEvidenceKinds.includes(completion.kind)) {
                throw new DomainValidationError("completion", "needs a known evidence kind");
            }
            return {
                patch: {
                    kind: "complete",
                    completionEvidenceKind: completion.kind,
                    completedByStaffUserId: staffUserIdOf(who),
                    completedAt: "now",
                },
                history: history(who, request, to, "COMPLETED", {
                    evidenceKind: "OPERATIONAL_CLOSURE",
                    internalReason: staffReason,
                }),
                auditAction: "concierge_request.completed",
            };
        }
        case "cancel":
        case "mark_unavailable":
        case "decline":
        case "expire":
            return closureEffect(matched, request, who, staffReason, evidence, now);
    }
}
function requireOutcome(outcome) {
    if (outcome === null || outcome === undefined) {
        throw new DomainValidationError("paymentOutcome", "is required");
    }
    return outcome;
}
const CLOSURE_EVENT = {
    cancel: "CANCELLED",
    mark_unavailable: "UNAVAILABLE",
    decline: "DECLINED",
    expire: "EXPIRED",
};
function closureEffect(matched, request, who, staffReason, evidence, now) {
    const command = matched.command;
    // A cancel while a payment is open waits for the payment to be settled one way or the other:
    // refunds are a separate concern.
    if (matched.from === "PAYMENT_REQUIRED") {
        const outcome = requireOutcome(evidence.paymentOutcome);
        if (outcome.status === "settled")
            throw violation("request.payment_already_settled");
        if (outcome.status === "pending" || outcome.status === "not_found") {
            throw violation("request.payment_outcome_pending");
        }
    }
    const expiredBySystem = command === "expire" && who.kind === "system";
    if (expiredBySystem &&
        (request.neededBy === null || request.neededBy.getTime() > now.getTime())) {
        throw violation("request.not_expired_yet");
    }
    const closureActorKind = who.kind === "customer" ? "CUSTOMER" : who.kind === "staff" ? "STAFF" : "SYSTEM";
    const summary = who.kind === "staff" &&
        evidence.customerStatusSummary !== null &&
        evidence.customerStatusSummary !== undefined
        ? boundedText("customerStatusSummary", evidence.customerStatusSummary, 500, {
            multiline: true,
        })
        : null;
    const leavingQuestion = matched.from === "AWAITING_CUSTOMER";
    const patch = {
        kind: "close",
        closedAt: "now",
        closureActorKind,
        ...(summary !== null ? { customerStatusSummary: summary } : {}),
        ...(leavingQuestion ? { pendingCustomerQuestion: null, awaitingCustomerSince: null } : {}),
        ...(matched.from === "IN_PROGRESS" ? { workStatus: "NONE" } : {}),
    };
    const auditAction = command === "cancel"
        ? who.kind === "customer"
            ? "concierge_request.cancelled_by_customer"
            : "concierge_request.cancelled"
        : command === "mark_unavailable"
            ? "concierge_request.unavailable"
            : command === "decline"
                ? "concierge_request.declined"
                : who.kind === "staff"
                    ? "concierge_request.expired_by_staff"
                    : "concierge_request.expired";
    return {
        patch,
        history: history(who, request, matched.to, CLOSURE_EVENT[command], {
            evidenceKind: expiredBySystem
                ? "EXPIRY_POLICY"
                : who.kind === "staff"
                    ? "OPERATIONAL_CLOSURE"
                    : null,
            internalReason: expiredBySystem ? "expiry:needed_by_elapsed" : staffReason,
        }),
        auditAction,
    };
}
/**
 * Who may open a draft and what it starts as. A customer creates their own (through the app or the
 * agent channel); staff create on a customer's behalf with a reason, when they hold the capability
 * and either an effective assignment to the customer or the supervisor role. A conversation, when
 * given, must belong to the same customer. Requirements are validated for the type.
 */
export function planRequestCreation(input) {
    const { actor, conversation } = input;
    if (conversation !== null && conversation.customerId !== input.customerId) {
        throw violation("request.conversation_mismatch");
    }
    let createdBy;
    let createdChannel;
    let createdByStaffUserId = null;
    let internalReason = null;
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== input.customerId)
                throw denied("not_owner");
            createdBy = "CUSTOMER";
            createdChannel = actor.channel;
            break;
        case "staff": {
            assertStaffCapability(actor, "request.create_for_customer", input.environment);
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            internalReason = requireReason(input.staffReason);
            createdBy = "STAFF";
            createdChannel = "STAFF_CONSOLE";
            createdByStaffUserId = actor.authority.staffUserId;
            break;
        }
        case "system":
            throw denied("role_missing");
    }
    const requirements = validateRequestRequirements(input.requestType, input.requirements);
    return Object.freeze({
        createdBy,
        createdChannel,
        createdByStaffUserId,
        conversationId: conversation?.id ?? null,
        requirements,
        history: Object.freeze({
            event: "CREATED",
            toState: "DRAFT",
            actorKind: actor.kind,
            actorId: transitionActorId(actor),
            channel: createdChannel,
            internalReason,
        }),
        auditAction: "concierge_request.created",
    });
}
// --- In-state commands ---------------------------------------------------------------------------
const DRAFT_EDITORS = {
    customer: true,
    staff: ["F", "S"],
    system: [],
    capability: "request.create_for_customer",
    staffCreatedOnly: true,
};
const OWNER_OR_SUPERVISOR = {
    customer: false,
    staff: ["O", "S"],
    system: [],
    capability: "request.work",
};
const SUPERVISOR_ONLY = {
    customer: false,
    staff: ["S"],
    system: [],
    capability: "request.reassign_owner",
};
/** The owner hands an owned request to a colleague (CHT-750): `request.work`, and the new holder must serve the customer. */
const OWNER_HANDOVER = {
    customer: false,
    staff: ["O"],
    system: [],
    capability: "request.work",
};
/**
 * Commands that change a request without changing its state: `update_draft` (DRAFT; the
 * customer, or staff for a staff-created draft), `reassign_owner` (a supervisor, or the owner to a
 * colleague who holds an effective assignment to the customer), `set_priority`
 * and `update_status_summary` (owner or supervisor, non-terminal). Each bumps the version and
 * writes one history row; the check order is the same as `planRequestTransition`: a staff actor is
 * checked against the command's single actor rule first (R24), then the state, then the actor
 * again as the rule's own check, `expectedVersion`, the reason and the evidence.
 */
export function planRequestInStateCommand(input) {
    const { request, command, actor, evidence } = input;
    const state = request.state;
    const actorRule = command === "update_draft"
        ? DRAFT_EDITORS
        : command === "reassign_owner"
            ? actor.kind === "staff" && isSupervisor(actor.authority)
                ? SUPERVISOR_ONLY
                : OWNER_HANDOVER
            : OWNER_OR_SUPERVISOR;
    // 0. Staff role and relationship, whatever the state (R24).
    assertStaffReachesRequest([actorRule], actor, input.environment);
    // 1. State: the lifecycle table says which states each command may run in (`request-lifecycle.ts`).
    const refusal = requestInStateRefusal(command, state);
    if (refusal !== null)
        throw violation(refusal);
    // 2. Actor, 3. version, 4. reason and evidence.
    const who = authorizeActor(actorRule, request, actor, input.environment);
    checkVersion(actor, input.expectedVersion, request);
    const staffReason = who.kind === "staff" ? requireReason(evidence.staffReason) : null;
    const make = (patch, event, auditAction, evidence = {}) => Object.freeze({
        command,
        state,
        patch,
        history: history(who, request, state, event, { internalReason: staffReason, ...evidence }),
        auditAction,
    });
    switch (command) {
        case "update_draft": {
            if (evidence.requirements === null || evidence.requirements === undefined) {
                throw new DomainValidationError("requirements", "is required");
            }
            const requirements = validateRequestRequirements(request.requirements.requestType, evidence.requirements);
            return make({ kind: "update_draft", requirements }, "DRAFT_UPDATED", who.kind === "staff"
                ? "concierge_request.draft_updated_by_staff"
                : "concierge_request.draft_updated");
        }
        case "reassign_owner": {
            const next = evidence.newAssignee;
            if (next === null || next === undefined)
                throw new DomainValidationError("newAssignee", "is required");
            if (!next.eligible)
                throw violation("request.assignee_ineligible");
            // The owner path (not a supervisor): the colleague must hold an effective assignment to the customer.
            if (actorRule === OWNER_HANDOVER && next.hasEffectiveAssignment !== true) {
                throw violation("request.assignee_ineligible");
            }
            const current = state === "ASSIGNED" ? request.assignedStaffUserId : request.ownerStaffUserId;
            if (next.staffUserId === current)
                throw violation("request.same_owner");
            const patch = state === "ASSIGNED"
                ? {
                    kind: "reassign_owner",
                    assignmentSource: "DIRECT",
                    assignedStaffUserId: next.staffUserId,
                    routeAssignmentId: null,
                }
                : { kind: "reassign_owner", ownerStaffUserId: next.staffUserId };
            // The history row names the new holder, so the ownership history reads from the rows alone (CHT-750).
            return make(patch, "OWNER_REASSIGNED", "concierge_request.owner_reassigned", {
                evidenceKind: "ASSIGNMENT_DECISION",
                evidenceReference: `DIRECT:${next.staffUserId}`,
            });
        }
        case "set_priority": {
            const priority = evidence.priority;
            if (priority === null || priority === undefined || !requestPriorities.includes(priority)) {
                throw new DomainValidationError("priority", "must be a known priority");
            }
            return make({ kind: "set_priority", priority }, "PRIORITY_CHANGED", "concierge_request.priority_changed");
        }
        case "update_status_summary": {
            const summary = boundedText("customerStatusSummary", evidence.customerStatusSummary, 500, {
                multiline: true,
            });
            return make({ kind: "update_status_summary", customerStatusSummary: summary }, "STATUS_SUMMARY_UPDATED", "concierge_request.status_summary_updated");
        }
        case "set_work_status": {
            const workStatus = evidence.workStatus;
            if (workStatus === null ||
                workStatus === undefined ||
                !requestWorkStatuses.includes(workStatus)) {
                throw new DomainValidationError("workStatus", "must be a known work status");
            }
            // A change that changes nothing would still bump the version and make a screen stale.
            if (workStatus === (request.workStatus ?? "NONE")) {
                throw new DomainValidationError("workStatus", "must differ from the current one");
            }
            return make({ kind: "set_work_status", workStatus }, "WORK_STATUS_CHANGED", "concierge_request.work_status_changed");
        }
    }
}
